<!-- Table Container -->
<div
    class="tw-flex tw-flex-col"
    [class.tw-h-full]="!maxHeight()"
    [style.max-height]="maxHeight()">
    <!-- Table Section -->
    <div
        class="tw-flex-1 tw-min-h-0 tw-overflow-x-auto tw-overflow-y-auto tw-relative">
        <!-- Loading Overlay -->
        @if (loading()) {
            <div
                class="tw-absolute tw-inset-0 tw-flex tw-items-center tw-justify-center tw-bg-white tw-bg-opacity-75 tw-z-10">
                <mat-progress-spinner
                    color="primary"
                    mode="indeterminate"
                    strokeWidth="3"
                    diameter="50">
                </mat-progress-spinner>
            </div>
        }

        <!-- Data Table -->
        <table
            matSort
            mat-table
            [dataSource]="dataSource()"
            [trackBy]="trackByFunction"
            multiTemplateDataRows
            class="tw-w-full tw-min-w-max generic-table"
            [class]="config().tableClass">
            @for (
                column of visibleColumns();
                track trackByColumn($index, column)
            ) {
                <ng-container [matColumnDef]="getColumnKey(column)">
                    <!-- Header Cell -->
                    <th
                        mat-header-cell
                        *matHeaderCellDef
                        [class]="column.cssClass"
                        [style.width]="column.width"
                        [style.text-align]="column.align || 'left'"
                        [class.tw-cursor-pointer]="column.sortable && sortable()"
                        [class.active-sort]="getSortDirection(column) !== ''"
                        (click)="
                            column.sortable && sortable() ? onSort(column) : null
                        ">
                        <!-- Special header for select column -->
                        @if (getColumnKey(column) === 'select') {
                            <mat-checkbox
                                [checked]="isAllSelected()"
                                [indeterminate]="
                                    hasSelection() && !isAllSelected()
                                "
                                (change)="toggleAllSelection()"
                                color="primary">
                            </mat-checkbox>
                        } @else {
                            <div class="tw-flex tw-items-center">
                                <span>{{ column.header | translate }}</span>
                                <!-- Sort Icon -->
                                @if (
                                    column.sortable &&
                                    sortable() &&
                                    getSortDirection(column) !== ''
                                ) {
                                    <mat-icon
                                        fontSet="material-symbols-outlined"
                                        class="tw-ml-1 tw-text-base">
                                        {{
                                            getSortDirection(column) === 'asc'
                                                ? 'arrow_upward'
                                                : 'arrow_downward'
                                        }}
                                    </mat-icon>
                                }
                            </div>
                        }
                        <!-- Regular header -->
                    </th>
                    <!-- Data Cell -->
                    <td
                        mat-cell
                        *matCellDef="let row; let i = index"
                        [class]="column.cssClass"
                        [style.text-align]="column.align || 'left'"
                        [class]="getRowClass(row)"
                        class="tw-pr-3">
                        <!-- Special handling for select column -->
                        @if (getColumnKey(column) === 'select') {
                            <div class="tw-text-center">
                                @if (
                                    !column.selectableCondition ||
                                    column.selectableCondition(row)
                                ) {
                                    <mat-checkbox
                                        [checked]="isRowSelected(row)"
                                        (change)="toggleRowSelection(row)"
                                        (click)="$event.stopPropagation()"
                                        color="primary">
                                    </mat-checkbox>
                                }
                            </div>
                        } @else {
                            @if (getColumnKey(column) === 'details') {
                                <div class="tw-text-center">
                                    @if (canRowExpand(row)) {
                                        <button
                                            mat-icon-button
                                            (click)="
                                                toggleRowExpansion(row);
                                                $event.stopPropagation()
                                            "
                                            [class.expanded]="
                                                isRowExpanded(row)
                                            "
                                            class="details-button">
                                            <mat-icon
                                                fontSet="material-symbols-outlined"
                                                [class.rotated]="
                                                    isRowExpanded(row)
                                                ">
                                                chevron_right
                                            </mat-icon>
                                        </button>
                                    }
                                </div>
                            } @else {
                                @if (
                                    getColumnKey(column) === 'expandedDetail'
                                ) {
                                    <!-- expandedDetail is handled separately below -->
                                } @else {
                                    <!-- Actions Content (check for custom component first) -->
                                    @if (getColumnKey(column) === 'actions') {
                                        <!-- Check if actions column has a custom component -->
                                        @if (column.component) {
                                            <ng-container
                                                *ngComponentOutlet="
                                                    column.component;
                                                    inputs: getComponentInputs(
                                                        row,
                                                        column
                                                    )
                                                ">
                                            </ng-container>
                                        } @else {
                                            <div
                                                class="tw-flex tw-items-center tw-space-x-1">
                                                @for (
                                                    action of getMainTableActions();
                                                    track action.id;
                                                    let actionIndex = $index
                                                ) {
                                                    <!-- Icon Button -->
                                                    @if (
                                                        action.variant ===
                                                            'icon' ||
                                                        !action.variant
                                                    ) {
                                                        <button
                                                            mat-icon-button
                                                            [color]="
                                                                action.color ||
                                                                'primary'
                                                            "
                                                            [disabled]="
                                                                action.disabled
                                                                    ? action.disabled(
                                                                          row
                                                                      )
                                                                    : false
                                                            "
                                                            [class]="
                                                                action.cssClass
                                                            "
                                                            [style.display]="
                                                                action.visible
                                                                    ? action.visible(
                                                                          row
                                                                      )
                                                                        ? 'inline-flex'
                                                                        : 'none'
                                                                    : 'inline-flex'
                                                            "
                                                            (click)="
                                                                action.handler(
                                                                    row
                                                                )
                                                            "
                                                            [matTooltip]="
                                                                action.translationKey
                                                                    ? (action.translationKey
                                                                      | translate)
                                                                    : (action.label
                                                                      | translate)
                                                            ">
                                                            <mat-icon
                                                                fontSet="material-symbols-outlined">
                                                                {{
                                                                    action.icon
                                                                }}
                                                            </mat-icon>
                                                        </button>
                                                    }
                                                    <!-- Raised Button -->
                                                    @if (
                                                        action.variant ===
                                                        'raised'
                                                    ) {
                                                        <button
                                                            mat-raised-button
                                                            color="primary"
                                                            [disabled]="
                                                                action.disabled
                                                                    ? action.disabled(
                                                                          row
                                                                      )
                                                                    : false
                                                            "
                                                            [class]="
                                                                action.cssClass
                                                            "
                                                            [style.display]="
                                                                action.visible
                                                                    ? action.visible(
                                                                          row
                                                                      )
                                                                        ? 'inline-flex'
                                                                        : 'none'
                                                                    : 'inline-flex'
                                                            "
                                                            (click)="
                                                                action.handler(
                                                                    row
                                                                )
                                                            ">
                                                            {{
                                                                action.translationKey
                                                                    ? (action.translationKey
                                                                      | translate)
                                                                    : (action.label
                                                                      | translate)
                                                            }}
                                                        </button>
                                                    }
                                                    <!-- Flat Button -->
                                                    @if (
                                                        action.variant ===
                                                        'flat'
                                                    ) {
                                                        <button
                                                            mat-flat-button
                                                            [color]="
                                                                action.color ||
                                                                'primary'
                                                            "
                                                            [disabled]="
                                                                action.disabled
                                                                    ? action.disabled(
                                                                          row
                                                                      )
                                                                    : false
                                                            "
                                                            [class]="
                                                                action.cssClass
                                                            "
                                                            [style.display]="
                                                                action.visible
                                                                    ? action.visible(
                                                                          row
                                                                      )
                                                                        ? 'inline-flex'
                                                                        : 'none'
                                                                    : 'inline-flex'
                                                            "
                                                            (click)="
                                                                action.handler(
                                                                    row
                                                                )
                                                            "
                                                            [matTooltip]="
                                                                action.translationKey
                                                                    ? (action.translationKey
                                                                      | translate)
                                                                    : (action.label
                                                                      | translate)
                                                            ">
                                                            @if (action.icon) {
                                                                <mat-icon
                                                                    fontSet="material-symbols-outlined">
                                                                    {{
                                                                        action.icon
                                                                    }}
                                                                </mat-icon>
                                                            }
                                                            @if (
                                                                !action.icon ||
                                                                action.label
                                                            ) {
                                                                <span>{{
                                                                    action.translationKey
                                                                        ? (action.translationKey
                                                                          | translate)
                                                                        : (action.label
                                                                          | translate)
                                                                }}</span>
                                                            }
                                                        </button>
                                                    }
                                                    <!-- Stroked Button -->
                                                    @if (
                                                        action.variant ===
                                                        'stroked'
                                                    ) {
                                                        <button
                                                            mat-stroked-button
                                                            [color]="
                                                                action.color ||
                                                                'primary'
                                                            "
                                                            [disabled]="
                                                                action.disabled
                                                                    ? action.disabled(
                                                                          row
                                                                      )
                                                                    : false
                                                            "
                                                            [class]="
                                                                action.cssClass
                                                            "
                                                            [style.display]="
                                                                action.visible
                                                                    ? action.visible(
                                                                          row
                                                                      )
                                                                        ? 'inline-flex'
                                                                        : 'none'
                                                                    : 'inline-flex'
                                                            "
                                                            (click)="
                                                                action.handler(
                                                                    row
                                                                )
                                                            "
                                                            [matTooltip]="
                                                                action.translationKey
                                                                    ? (action.translationKey
                                                                      | translate)
                                                                    : (action.label
                                                                      | translate)
                                                            ">
                                                            @if (action.icon) {
                                                                <mat-icon
                                                                    fontSet="material-symbols-outlined">
                                                                    {{
                                                                        action.icon
                                                                    }}
                                                                </mat-icon>
                                                            }
                                                            @if (
                                                                !action.icon ||
                                                                action.label
                                                            ) {
                                                                <span>{{
                                                                    action.translationKey
                                                                        ? (action.translationKey
                                                                          | translate)
                                                                        : (action.label
                                                                          | translate)
                                                                }}</span>
                                                            }
                                                        </button>
                                                    }
                                                }
                                            </div>
                                        }
                                        <!-- Default action handling with actionConfig -->
                                    } @else {
                                        <!-- Custom Template -->
                                        @if (getColumnTemplate(column)) {
                                            <ng-container
                                                *ngTemplateOutlet="
                                                    getColumnTemplate(column);
                                                    context: {
                                                        $implicit: row,
                                                        column: column,
                                                        value: getCellValue(
                                                            row,
                                                            column
                                                        ),
                                                        index: i,
                                                    }
                                                ">
                                            </ng-container>
                                        } @else {
                                            @if (column.component) {
                                                <ng-container
                                                    *ngComponentOutlet="
                                                        column.component;
                                                        inputs: getComponentInputs(
                                                            row,
                                                            column
                                                        )
                                                    ">
                                                </ng-container>
                                            } @else {
                                                @if (!column.pipe) {
                                                    <span
                                                        [innerHTML]="
                                                            getFormattedValue(
                                                                row,
                                                                column
                                                            )
                                                        "></span>
                                                }
                                                @if (
                                                    column.pipe &&
                                                    column.pipe === 'date'
                                                ) {
                                                    <span
                                                        [innerHTML]="
                                                            getCellValue(
                                                                row,
                                                                column
                                                            )
                                                                | date
                                                                    : column
                                                                          .pipeArgs?.[0] ||
                                                                          'medium'
                                                        "></span>
                                                }
                                                @if (
                                                    column.pipe &&
                                                    column.pipe !== 'date'
                                                ) {
                                                    <span
                                                        [innerHTML]="
                                                            getCellValue(
                                                                row,
                                                                column
                                                            )
                                                        "></span>
                                                }
                                            }
                                        }
                                        <!-- Custom Component -->
                                        <!-- Default Cell -->
                                    }
                                    <!-- Non-Action Cell Content -->
                                }
                            }
                        }
                        <!-- Special handling for details column -->
                        <!-- Special handling for expandedDetail column -->
                        <!-- Regular cell content -->
                    </td>
                </ng-container>
            }

            <!-- Expanded Detail Column -->
            @if (expandable() || config()?.features?.expandable) {
                <ng-container matColumnDef="expandedDetail">
                    <td
                        mat-cell
                        *matCellDef="let row"
                        [attr.colspan]="displayedColumns().length"
                        class="expanded-detail-cell">
                        <div
                            class="dls-element-detail"
                            [@expandCollapse]="
                                isRowExpanded(row) ? 'expanded' : 'collapsed'
                            ">
                            <!-- Custom template for expanded content -->
                            @if (config()?.expandedRowConfig?.template) {
                                <ng-container
                                    *ngTemplateOutlet="
                                        config()?.expandedRowConfig?.template;
                                        context: {
                                            $implicit: row,
                                            data: getExpandedData(row),
                                            expanded: isRowExpanded(row),
                                        }
                                    ">
                                </ng-container>
                            } @else {
                                @if (config()?.expandedRowConfig?.tableConfig) {
                                    <div class="expanded-table-container">
                                        <!-- Loading state -->
                                        @if (isExpandedRowLoading(row)) {
                                            <div
                                                class="tw-flex tw-items-center tw-justify-center tw-py-4">
                                                <mat-progress-spinner
                                                    color="primary"
                                                    mode="indeterminate"
                                                    strokeWidth="2"
                                                    diameter="24">
                                                </mat-progress-spinner>
                                            </div>
                                        }
                                        <!-- Table Layout (respects content width) -->
                                        @if (!isExpandedRowLoading(row)) {
                                            <div>
                                                <!-- Content with data - using table layout -->
                                                @if (
                                                    getExpandedRowData(row)
                                                        .length > 0
                                                ) {
                                                    <div
                                                        class="expanded-data-table tw-w-full tw-overflow-hidden">
                                                        <table
                                                            class="tw-w-full tw-table-auto">
                                                            <!-- Header Row -->
                                                            <thead>
                                                                <tr
                                                                    class="tw-bg-slate-50">
                                                                    @for (
                                                                        column of getExpandedRowColumns();
                                                                        track column.key
                                                                    ) {
                                                                        <th
                                                                            class="tw-text-left tw-text-xs tw-font-semibold tw-text-gray-700 tw-uppercase tw-tracking-wider tw-py-3 tw-px-4 tw-border-r tw-border-gray-200"
                                                                            [style.width]="
                                                                                column.width ||
                                                                                'auto'
                                                                            ">
                                                                            @if (
                                                                                column.key ===
                                                                                'actions'
                                                                            ) {
                                                                                {{
                                                                                    'OUTGOING-GOODS.FOLDER-VIEW.TABLE-HEADER.ACTIONS'
                                                                                        | translate
                                                                                }}
                                                                            } @else {
                                                                                {{
                                                                                    column.header
                                                                                        | translate
                                                                                }}
                                                                            }
                                                                        </th>
                                                                    }
                                                                </tr>
                                                            </thead>
                                                            <!-- Data Rows -->
                                                            <tbody>
                                                                @for (
                                                                    item of getExpandedRowData(
                                                                        row
                                                                    );
                                                                    track $index;
                                                                    let i = $index
                                                                ) {
                                                                    <tr
                                                                        class="tw-border-b tw-border-gray-100 hover:tw-bg-gray-50">
                                                                        @for (
                                                                            column of getExpandedRowColumns();
                                                                            track column.key
                                                                        ) {
                                                                            <td
                                                                                class="tw-py-2 tw-px-4 tw-border-r tw-border-gray-100 tw-align-top tw-break-words"
                                                                                [style.width]="
                                                                                    column.width ||
                                                                                    'auto'
                                                                                ">
                                                                                <!-- Actions Content (check for custom component first) -->
                                                                                @if (
                                                                                    column.key ===
                                                                                    'actions'
                                                                                ) {
                                                                                    <div
                                                                                        class="tw-flex tw-items-center tw-space-x-1">
                                                                                        <!-- Check if actions column has a custom component -->
                                                                                        @if (
                                                                                            column.component
                                                                                        ) {
                                                                                            <ng-container
                                                                                                *ngComponentOutlet="
                                                                                                    column.component;
                                                                                                    inputs: getExpandedComponentInputs(
                                                                                                        item,
                                                                                                        column,
                                                                                                        row
                                                                                                    )
                                                                                                ">
                                                                                            </ng-container>
                                                                                        } @else {
                                                                                            @for (
                                                                                                action of getExpandedRowActions();
                                                                                                track action.id
                                                                                            ) {
                                                                                                <!-- Icon Button -->
                                                                                                @if (
                                                                                                    action.variant ===
                                                                                                        'icon' ||
                                                                                                    !action.variant
                                                                                                ) {
                                                                                                    <button
                                                                                                        mat-icon-button
                                                                                                        [color]="
                                                                                                            action.color ||
                                                                                                            'primary'
                                                                                                        "
                                                                                                        [disabled]="
                                                                                                            action.disabled
                                                                                                                ? action.disabled(
                                                                                                                      item
                                                                                                                  )
                                                                                                                : false
                                                                                                        "
                                                                                                        [class]="
                                                                                                            action.cssClass
                                                                                                        "
                                                                                                        [style.display]="
                                                                                                            action.visible
                                                                                                                ? action.visible(
                                                                                                                      item
                                                                                                                  )
                                                                                                                    ? 'inline-flex'
                                                                                                                    : 'none'
                                                                                                                : 'inline-flex'
                                                                                                        "
                                                                                                        (click)="
                                                                                                            action.handler(
                                                                                                                item
                                                                                                            )
                                                                                                        "
                                                                                                        [matTooltip]="
                                                                                                            action.translationKey
                                                                                                                | translate
                                                                                                        "
                                                                                                        class="modern-action-button">
                                                                                                        <mat-icon
                                                                                                            fontSet="material-symbols-outlined">
                                                                                                            {{
                                                                                                                action.icon
                                                                                                            }}
                                                                                                        </mat-icon>
                                                                                                    </button>
                                                                                                }
                                                                                                <!-- Raised Button -->
                                                                                                @if (
                                                                                                    action.variant ===
                                                                                                    'raised'
                                                                                                ) {
                                                                                                    <button
                                                                                                        mat-raised-button
                                                                                                        color="primary"
                                                                                                        [disabled]="
                                                                                                            action.disabled
                                                                                                                ? action.disabled(
                                                                                                                      item
                                                                                                                  )
                                                                                                                : false
                                                                                                        "
                                                                                                        [class]="
                                                                                                            action.cssClass
                                                                                                        "
                                                                                                        [style.display]="
                                                                                                            action.visible
                                                                                                                ? action.visible(
                                                                                                                      item
                                                                                                                  )
                                                                                                                    ? 'inline-flex'
                                                                                                                    : 'none'
                                                                                                                : 'inline-flex'
                                                                                                        "
                                                                                                        (click)="
                                                                                                            action.handler(
                                                                                                                item
                                                                                                            )
                                                                                                        ">
                                                                                                        {{
                                                                                                            action.translationKey
                                                                                                                | translate
                                                                                                        }}
                                                                                                    </button>
                                                                                                }
                                                                                                <!-- Flat Button -->
                                                                                                @if (
                                                                                                    action.variant ===
                                                                                                    'flat'
                                                                                                ) {
                                                                                                    <button
                                                                                                        mat-flat-button
                                                                                                        [color]="
                                                                                                            action.color ||
                                                                                                            'primary'
                                                                                                        "
                                                                                                        [disabled]="
                                                                                                            action.disabled
                                                                                                                ? action.disabled(
                                                                                                                      item
                                                                                                                  )
                                                                                                                : false
                                                                                                        "
                                                                                                        [class]="
                                                                                                            action.cssClass
                                                                                                        "
                                                                                                        [style.display]="
                                                                                                            action.visible
                                                                                                                ? action.visible(
                                                                                                                      item
                                                                                                                  )
                                                                                                                    ? 'inline-flex'
                                                                                                                    : 'none'
                                                                                                                : 'inline-flex'
                                                                                                        "
                                                                                                        (click)="
                                                                                                            action.handler(
                                                                                                                item
                                                                                                            )
                                                                                                        "
                                                                                                        [matTooltip]="
                                                                                                            action.label
                                                                                                                | translate
                                                                                                        "
                                                                                                        class="modern-action-button">
                                                                                                        @if (
                                                                                                            action.icon
                                                                                                        ) {
                                                                                                            <mat-icon
                                                                                                                fontSet="material-symbols-outlined"
                                                                                                                [fontIcon]="
                                                                                                                    action.icon
                                                                                                                ">
                                                                                                            </mat-icon>
                                                                                                        }
                                                                                                        @if (
                                                                                                            !action.icon ||
                                                                                                            action.label
                                                                                                        ) {
                                                                                                            <span
                                                                                                                >{{
                                                                                                                    action.label
                                                                                                                        | translate
                                                                                                                }}</span
                                                                                                            >
                                                                                                        }
                                                                                                    </button>
                                                                                                }
                                                                                                <!-- Stroked Button -->
                                                                                                @if (
                                                                                                    action.variant ===
                                                                                                    'stroked'
                                                                                                ) {
                                                                                                    <button
                                                                                                        mat-stroked-button
                                                                                                        [color]="
                                                                                                            action.color ||
                                                                                                            'primary'
                                                                                                        "
                                                                                                        [disabled]="
                                                                                                            action.disabled
                                                                                                                ? action.disabled(
                                                                                                                      item
                                                                                                                  )
                                                                                                                : false
                                                                                                        "
                                                                                                        [class]="
                                                                                                            action.cssClass
                                                                                                        "
                                                                                                        [style.display]="
                                                                                                            action.visible
                                                                                                                ? action.visible(
                                                                                                                      item
                                                                                                                  )
                                                                                                                    ? 'inline-flex'
                                                                                                                    : 'none'
                                                                                                                : 'inline-flex'
                                                                                                        "
                                                                                                        (click)="
                                                                                                            action.handler(
                                                                                                                item
                                                                                                            )
                                                                                                        "
                                                                                                        [matTooltip]="
                                                                                                            action.label
                                                                                                                | translate
                                                                                                        "
                                                                                                        class="modern-action-button">
                                                                                                        @if (
                                                                                                            action.icon
                                                                                                        ) {
                                                                                                            <mat-icon
                                                                                                                fontSet="material-symbols-outlined"
                                                                                                                [fontIcon]="
                                                                                                                    action.icon
                                                                                                                ">
                                                                                                            </mat-icon>
                                                                                                        }
                                                                                                        @if (
                                                                                                            !action.icon ||
                                                                                                            action.label
                                                                                                        ) {
                                                                                                            <span
                                                                                                                >{{
                                                                                                                    action.label
                                                                                                                        | translate
                                                                                                                }}</span
                                                                                                            >
                                                                                                        }
                                                                                                    </button>
                                                                                                }
                                                                                            }
                                                                                        }
                                                                                        <!-- Default action handling with actionConfig -->
                                                                                    </div>
                                                                                }
                                                                                <!-- Data Content -->
                                                                                @if (
                                                                                    column.key !==
                                                                                    'actions'
                                                                                ) {
                                                                                    <!-- Custom Template -->
                                                                                    @if (
                                                                                        column.template
                                                                                    ) {
                                                                                        <ng-container
                                                                                            *ngTemplateOutlet="
                                                                                                column.template;
                                                                                                context: {
                                                                                                    $implicit:
                                                                                                        item,
                                                                                                    column: column,
                                                                                                    value: item[
                                                                                                        column
                                                                                                            .key
                                                                                                    ],
                                                                                                    parentRow:
                                                                                                        row,
                                                                                                }
                                                                                            ">
                                                                                        </ng-container>
                                                                                    } @else {
                                                                                        @if (
                                                                                            column.component
                                                                                        ) {
                                                                                            <ng-container
                                                                                                *ngComponentOutlet="
                                                                                                    column.component;
                                                                                                    inputs: getExpandedComponentInputs(
                                                                                                        item,
                                                                                                        column,
                                                                                                        row
                                                                                                    )
                                                                                                ">
                                                                                            </ng-container>
                                                                                        } @else {
                                                                                            @if (
                                                                                                item[
                                                                                                    column
                                                                                                        .key
                                                                                                ] &&
                                                                                                !column.pipe
                                                                                            ) {
                                                                                                <span
                                                                                                    class="tw-text-sm tw-text-gray-900 tw-break-words">
                                                                                                    {{
                                                                                                        item[
                                                                                                            column
                                                                                                                .key
                                                                                                        ]
                                                                                                    }}
                                                                                                </span>
                                                                                            }
                                                                                            @if (
                                                                                                item[
                                                                                                    column
                                                                                                        .key
                                                                                                ] &&
                                                                                                column.pipe &&
                                                                                                column.pipe ===
                                                                                                    'date'
                                                                                            ) {
                                                                                                <span
                                                                                                    class="tw-text-sm tw-text-gray-900 tw-break-words">
                                                                                                    {{
                                                                                                        item[
                                                                                                            column
                                                                                                                .key
                                                                                                        ]
                                                                                                            | date
                                                                                                                : column
                                                                                                                      .pipeArgs?.[0] ||
                                                                                                                      'medium'
                                                                                                    }}
                                                                                                </span>
                                                                                            }
                                                                                            @if (
                                                                                                item[
                                                                                                    column
                                                                                                        .key
                                                                                                ] &&
                                                                                                column.pipe &&
                                                                                                column.pipe !==
                                                                                                    'date'
                                                                                            ) {
                                                                                                <span
                                                                                                    class="tw-text-sm tw-text-gray-900 tw-break-words">
                                                                                                    {{
                                                                                                        item[
                                                                                                            column
                                                                                                                .key
                                                                                                        ]
                                                                                                    }}
                                                                                                </span>
                                                                                            }
                                                                                            @if (
                                                                                                !item[
                                                                                                    column
                                                                                                        .key
                                                                                                ]
                                                                                            ) {
                                                                                                <span
                                                                                                    class="tw-text-sm tw-text-gray-400 tw-italic">
                                                                                                    —
                                                                                                </span>
                                                                                            }
                                                                                        }
                                                                                    }
                                                                                    <!-- Custom Component -->
                                                                                    <!-- Default Cell -->
                                                                                }
                                                                            </td>
                                                                        }
                                                                    </tr>
                                                                }
                                                            </tbody>
                                                        </table>
                                                    </div>
                                                }
                                                <!-- No data state -->
                                                @if (
                                                    getExpandedRowData(row)
                                                        .length === 0
                                                ) {
                                                    <div
                                                        class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-py-8 tw-text-center">
                                                        <mat-icon
                                                            class="tw-text-2xl tw-text-gray-300 tw-mb-2"
                                                            >info</mat-icon
                                                        >
                                                        <div
                                                            class="tw-text-sm tw-text-gray-500">
                                                            {{
                                                                config()
                                                                    ?.expandedRowConfig
                                                                    ?.tableConfig
                                                                    ?.emptyMessage ||
                                                                    ('OUTGOING-GOODS.TABLE.EMPTY'
                                                                        | translate)
                                                            }}
                                                        </div>
                                                    </div>
                                                }
                                            </div>
                                        }
                                    </div>
                                } @else {
                                    <div
                                        class="tw-text-sm tw-text-gray-600 tw-p-4">
                                        {{
                                            'GENERAL.EXPANDED-CONTENT-PLACEHOLDER'
                                                | translate
                                        }}
                                    </div>
                                    <pre class="tw-text-xs tw-mt-2 tw-p-4">{{
                                        row | json
                                    }}</pre>
                                }
                            }
                            <!-- Table configuration for expanded content -->
                            <!-- Default expanded content -->
                        </div>
                    </td>
                </ng-container>
            }

            <!-- Header and Row Definitions -->
            <tr
                mat-header-row
                *matHeaderRowDef="displayedColumns(); sticky: true"></tr>
            <tr
                mat-row
                *matRowDef="let row; columns: displayedColumns(); let i = index"
                [class]="getRowClass(row)"
                [class.tw-cursor-pointer]="config().rowClass"
                [class.expanded-row]="isRowExpanded(row)"
                class="dls-element-row custom-row-border"></tr>
            @if (expandable() || config()?.features?.expandable) {
                <tr
                    mat-row
                    *matRowDef="let row; columns: ['expandedDetail']"
                    class="expanded-detail-row"
                    [class]="config()?.cssClasses?.expandedRow"></tr>
            }
        </table>

        <!-- Empty State -->
        @if (data().length === 0 && !loading()) {
            <div
                class="tw-flex tw-flex-col tw-items-center tw-justify-center tw-h-64 tw-text-center"
                [class.tw-bg-red-50]="error()"
                [class.tw-border-l-4]="error()"
                [class.tw-border-red-500]="error()">
                <!-- Error State Icon -->
                @if (error()) {
                    <mat-icon
                        class="tw-ml-3 tw-text-red-500 tw-mb-4"
                        svgIcon="red-error">
                    </mat-icon>
                }
                <!-- Normal State Icon -->
                @if (!error()) {
                    <mat-icon
                        fontSet="material-symbols-outlined"
                        class="tw-text-gray-400 tw-mb-4">
                        error
                    </mat-icon>
                }
                <h3
                    class="tw-text-lg tw-font-medium tw-mb-2 tw-text-center"
                    [class.tw-text-red-700]="error()">
                    {{
                        (error() ? getErrorMessage() : emptyStateMessage())
                            | translate
                    }}
                </h3>
                <p
                    class="tw-text-gray-600 tw-mb-4 tw-whitespace-pre-line tw-text-center"
                    [class.tw-text-red-600]="error()">
                    {{
                        (error()
                            ? getErrorDescriptionKey()
                            : 'GENERAL.NO-DATA-DESCRIPTION'
                        ) | translate: { errorCode: getErrorCode() }
                    }}
                </p>
                <button
                    mat-button
                    color="primary"
                    (click)="refreshData()">
                    {{ 'GENERAL.REFRESH' | translate }}
                </button>
            </div>
        }
    </div>

    <!-- Footer row: paginator (centered) with auto-reload button overlaid on the right -->
    @if (
        autoReloadEnabled() ||
        (showPagination() && config().features?.pagination !== false)
    ) {
        <div class="tw-flex-shrink-0 tw-relative">
            @if (showPagination() && config().features?.pagination !== false) {
                <app-table-pagination
                    [loading]="loading()"
                    [pageSizeOptions]="paginationOptions()"
                    (pageChange)="onPageChange($event)">
                </app-table-pagination>
                @if (autoReloadEnabled()) {
                    <button
                        mat-icon-button
                        type="button"
                        color="primary"
                        class="tw-absolute tw-right-2 tw-top-1/2 -tw-translate-y-1/2 tw-z-10"
                        [matTooltip]="'GENERAL.REFRESH' | translate"
                        [matBadge]="countdownSeconds()"
                        [matBadgeHidden]="countdownSeconds() === null"
                        matBadgeSize="small"
                        matBadgeOverlap="true"
                        matBadgeColor="accent"
                        (click)="onAutoReloadButtonClick()">
                        <mat-icon fontSet="material-symbols-outlined">
                            refresh
                        </mat-icon>
                    </button>
                }
            } @else if (autoReloadEnabled()) {
                <div
                    class="tw-flex tw-justify-end tw-px-4 tw-py-3 tw-border-t tw-border-gray-200 tw-bg-white">
                    <button
                        mat-icon-button
                        type="button"
                        color="primary"
                        [matTooltip]="'GENERAL.REFRESH' | translate"
                        [matBadge]="countdownSeconds()"
                        [matBadgeHidden]="countdownSeconds() === null"
                        matBadgeSize="small"
                        matBadgeOverlap="true"
                        matBadgeColor="accent"
                        (click)="onAutoReloadButtonClick()">
                        <mat-icon fontSet="material-symbols-outlined">
                            refresh
                        </mat-icon>
                    </button>
                </div>
            }
        </div>
    }

    <!-- Custom Content Slots -->
    <ng-content select="[slot=footer]"></ng-content>
</div>
